import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  signIn,
  signOut,
  refreshAccess,
  fetchCurrentUser,
} from '../../core/session/session-controller'
import type { LoginInput } from '../contract'

const authKeys = {
  all: ['auth'] as const,
  currentUser: ['auth', 'currentUser'] as const,
}

/**
 * GET /api/auth/me — the authoritative signed-in profile (capabilities,
 * features). Goes through the authed/refresh path; a 401 clears the session and
 * surfaces here as an error, never a fabricated identity. `retry: false` so a
 * genuine 401 is not hammered.
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: authKeys.currentUser,
    queryFn: fetchCurrentUser,
    retry: false,
  })
}

/** POST /api/auth/login → establish the session. Returns the REAL API user. */
export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: LoginInput) => signIn(data),
    onSuccess: () => {
      // Force /api/auth/me to re-run with the new access token on the landing.
      void queryClient.invalidateQueries({ queryKey: authKeys.currentUser })
    },
  })
}

/**
 * Silent token rotation. Exposed for completeness; the transport already
 * refreshes on 401 automatically (session-controller.authedFetch). Resolves to
 * whether a fresh access token is now held — it does not throw on a normal
 * end-of-session.
 */
export function useRefresh() {
  return useMutation({
    mutationFn: () => refreshAccess(),
  })
}

/** POST /api/auth/logout → revoke server-side (best effort) + clear locally. */
export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => signOut(),
    onSettled: () => {
      // The session is gone regardless of the server's answer — drop any cached
      // identity so a later login cannot read a stale user.
      queryClient.removeQueries({ queryKey: authKeys.all })
    },
  })
}
